import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { DATE_RANGES, isFilterActive } from "../../utils/sessionFilters";

// How long to wait after the last keystroke before asking the server. Long
// enough that typing "deadlift" is one request rather than eight, short enough
// that it still feels like the list is answering you.
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The Activity list's filter controls.
 *
 * The search box is locally controlled and debounced upward: the input has to
 * repaint on every keystroke, but the fetch it triggers must not. The range
 * select is not debounced — it is a single deliberate choice, so it applies at
 * once.
 */
const SessionFilterBar = ({ filter, onChange, resultCount, loading }) => {
  const [term, setTerm] = useState(filter.q);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Keep the box in step when the filter is cleared from outside (the "no
  // matches" empty state offers its own Clear).
  useEffect(() => {
    setTerm(filter.q);
  }, [filter.q]);

  useEffect(() => {
    if (term === filter.q) return;
    const id = setTimeout(
      () => onChangeRef.current({ ...filter, q: term }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [term, filter]);

  const active = isFilterActive(filter);

  return (
    <div className="space-y-2 mb-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search"
            aria-label="Search sessions, notes or exercises"
            className="h-9 pl-8 text-sm"
          />
        </div>
        <Select
          value={filter.range}
          onValueChange={(range) => onChange({ ...filter, range })}
        >
          <SelectTrigger
            className="h-9 w-[9.5rem] shrink-0 text-sm"
            aria-label="Date range"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Only while something is actually narrowed — an unfiltered list should
          not carry a line telling you it is unfiltered. */}
      {active && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span aria-live="polite">
            {loading
              ? "Searching…"
              : `${resultCount} ${resultCount === 1 ? "session" : "sessions"} found`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange({ q: "", range: "all" })}
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default SessionFilterBar;
